/* eslint-disable @typescript-eslint/no-explicit-any */
import classNames from 'classnames';
import { HtmlHTMLAttributes, ReactNode, useCallback, useMemo } from 'react';
import { Button, Checkbox } from 'rsuite';
import { ButtonProps } from 'rsuite/lib/Button';
import { CheckPickerProps } from 'rsuite/lib/CheckPicker';
import { CheckboxProps } from 'rsuite/lib/Checkbox';
import { SelectPickerProps } from 'rsuite/lib/SelectPicker';
import { SalvarFiltrosModal } from '../../../pages/IPA/Negociacoes/pages/NegociacaoFornecedor/components';
import { ExcluirFiltroModal, Icon } from '../../../pages/IPA/RevisaoPrecos/Components';
import { SelectPill } from '../../../pages/IRA/Components/SelectPill';
import { SegmentsPlaceholder } from '../../NewFilterBox/Components';
import { IFilterItem, IUseFilter } from '../hooks';
import { usePasteValuesMenu } from '../usePasteValuesMenu';
import styles from './FilterSection.module.scss';

export type FilterSelectAllProps = CheckboxProps & {
  onSelectAll?: (checked: boolean) => void;
};

export const FilterSelectAll = ({ className, checked, indeterminate, onChange, onSelectAll, ...props }: FilterSelectAllProps) => {
  return (
    <div className={classNames('picker-pill-footer', className)}>
      <Checkbox
        inline
        onChange={(v, c, e) => {
          onSelectAll?.(c);
          onChange?.(v, c, e);
        }}
        checked={checked}
        indeterminate={checked ? false : indeterminate}
        {...props}
      >
        Selecionar todos
      </Checkbox>
    </div>
  );
};

export type CheckFilterProps = CheckPickerProps & Omit<IFilterItem, 'queryFn'>;

const Check = ({ className, type = 'check', ...props }: CheckFilterProps) => <SelectPill id="check-picker-pill" className={className} type={type} {...props} />;

type SelectFilterProps = SelectPickerProps & Omit<IFilterItem, 'queryFn'>;

const Select = ({ className, type = 'select', ...props }: SelectFilterProps) => (
  <SelectPill id="check-picker-pill" className={className} type={type} {...props} />
);

type BaseProps = {
  isLoading?: boolean;
  textLoading?: string;
  textSearch?: string;
  selectAll?: FilterSelectAllProps;
  menuAutoWidth?: boolean;
  search?: string;
  /** Mostra o botão "Colar lista de códigos" no menu, igual ao filtro de produtos do Gerenciador */
  pasteValues?: boolean;
  onConfirmPasteValues?: (query: string) => void;
  modalText?: string;
};

type SharedFilterProps = Omit<CheckFilterProps, 'type'> & Omit<SelectFilterProps, 'type'>;

type InnerComponentProps = {
  type?: 'check' | 'select';
} & SharedFilterProps &
  BaseProps;

const SAVED_FILTER_MENU_PLACEMENT = 'bottomEnd' as const;

const FILTERS = {
  check: (props: CheckFilterProps) => <Check {...props} />,
  select: (props: SelectFilterProps) => <Select {...props} />,
} as const;

export const InnerFilter = ({
  data = [],
  value = [],
  textLoading = 'Carregando...',
  textSearch = 'Pesquisar',
  type = 'check',
  selectAll,
  isLoading = false,
  pasteValues = false,
  searchable = true,
  menuAutoWidth,
  search,
  modalText,
  renderMenu,
  onSearch,
  onConfirmPasteValues,
  onClose,
  ...props
}: InnerComponentProps) => {
  const pasteMenu = usePasteValuesMenu({ enabled: pasteValues, search, modalText, onSearch, onConfirm: onConfirmPasteValues });

  const renderSearchable = useMemo(() => {
    return pasteValues ? false : searchable;
  }, [searchable, pasteValues]);

  const renderPasteMenu = pasteMenu.renderMenu;
  const renderCustomMenu = useMemo(() => {
    if (!pasteValues) return renderMenu;
    return (menu: ReactNode) => renderPasteMenu(renderMenu ? renderMenu(menu) : menu);
  }, [pasteValues, renderMenu, renderPasteMenu]);

  const renderLocale = useMemo(
    () => ({
      noResultsText: isLoading ? textLoading : 'Nenhum item encontrado',
      searchPlaceholder: textSearch,
    }),
    [isLoading, textLoading, textSearch],
  );

  const renderExtraFooter = useCallback(() => {
    return selectAll ? <FilterSelectAll checked={data?.length === value?.length && !!value?.length} {...selectAll} /> : null;
  }, [data?.length, selectAll, value?.length]);

  const RenderComponent = FILTERS[type] || null;

  return (
    <>
      <RenderComponent
        data={isLoading ? [] : data}
        value={value}
        searchable={renderSearchable}
        locale={renderLocale}
        renderExtraFooter={renderExtraFooter}
        menuAutoWidth={menuAutoWidth}
        renderMenu={renderCustomMenu}
        onSearch={pasteValues ? pasteMenu.handleSearch : onSearch}
        {...props}
      />
      {pasteMenu.modal}
    </>
  );
};

export const FilterButton = ({ className, ...props }: ButtonProps) => <Button className={classNames(styles['filter-section__button'], className)} {...props} />;

// Só a tela que passa `onApply` ganha o Aplicar; as demais aplicam a cada escolha, como antes.
type ApplyFilterProps = {
  onApply?: () => void;
  hasPendingFilter?: boolean;
};

export type FilterSectionProps = IUseFilter & ApplyFilterProps & Omit<HtmlHTMLAttributes<HTMLDivElement>, 'onSelect'>;

const ClearAllButton = ({ onClearAll }: Pick<IUseFilter, 'onClearAll'>) => (
  <Button className={styles['filter-section__button']} onClick={onClearAll}>
    Limpar filtros
  </Button>
);

const FilterSection = ({
  className,
  data = {},
  values = {},
  savedFilter,
  filters = [],
  isEmpty = true,
  modals,
  loading,
  onSearch,
  onSelect,
  onClearAll,
  onSelectAll,
  onOpen,
  onOpenModal,
  onCloseModal,
  onSaveFilter,
  onDeleteFilter,
  onSelectSavedFilter,
  onApply,
  hasPendingFilter = false,
  ...props
}: FilterSectionProps) => (
  <>
    <div className={classNames(styles['filter-section'], className)} {...props}>
      <div className={classNames(styles.row, styles.row1)}>
        <div>
          {filters.map(
            ({ key, placeholder, type, ...rest }) =>
              !key.match(/category/) &&
              !key.match(/savedFilters/) && (
                <InnerFilter
                  key={key}
                  data={data[key] ?? []}
                  value={type === 'select' ? (values[key]?.[0] ?? null) : (values[key] ?? [])}
                  placeholder={placeholder as string}
                  type={type}
                  isLoading={loading?.[key]}
                  textSearch={rest.textSearch}
                  textLoading={rest.textLoading}
                  onSearch={(query: string) => onSearch(key, query)}
                  onSelect={(value: string[]) => onSelect(key, value)}
                  onOpen={() => onOpen(key)}
                  onClose={() => onSearch(key, '')}
                  onClean={() => {
                    onSearch(key, '');
                    onSelect(key, []);
                  }}
                  renderExtraFooter={
                    rest.selectAll
                      ? () => {
                          const isChecked = data[key]?.length === values[key]?.length;
                          return (
                            <div className="picker-pill-footer">
                              <Checkbox
                                inline
                                indeterminate={!isChecked && !!values[key]?.length}
                                checked={isChecked}
                                onChange={(_v, checked) => onSelectAll?.(key, checked)}
                              >
                                Selecionar todos
                              </Checkbox>
                            </div>
                          );
                        }
                      : undefined
                  }
                />
              ),
          )}
        </div>
        {onApply ? (
          <div>
            {onClearAll && <ClearAllButton onClearAll={onClearAll} />}
            <Button
              className={styles['filter-section__apply']}
              appearance={hasPendingFilter ? 'primary' : 'default'}
              disabled={!hasPendingFilter}
              onClick={onApply}
            >
              Aplicar
            </Button>
          </div>
        ) : (
          onClearAll && <ClearAllButton onClearAll={onClearAll} />
        )}
      </div>
      <div className={classNames(styles.row, styles.row2)}>
        <div>
          {filters.map(
            ({ key, placeholder, type, level, ...rest }) =>
              key.match(/categoryLevel/) && (
                <InnerFilter
                  key={key}
                  data={data[key] ?? []}
                  value={values[key] ?? []}
                  isLoading={loading?.[key]}
                  textSearch={rest.textSearch}
                  textLoading={rest.textLoading}
                  type={type}
                  onSearch={(query: string) => onSearch(key, query)}
                  onSelect={(value: string[]) => onSelect(key, value)}
                  onOpen={() => onOpen(key)}
                  onClose={() => onSearch(key, '')}
                  onClean={() => {
                    onSearch(key, '');
                    onSelect(key, []);
                  }}
                  // @ts-ignore
                  placeholder={<SegmentsPlaceholder level={level as number} placeholder={placeholder as string} />}
                />
              ),
          )}
        </div>

        <div>
          {!isEmpty && (
            <Button onClick={() => onOpenModal?.('salvarFiltros')}>
              <Icon icon="MdSave" />
            </Button>
          )}
          {savedFilter.value && (
            <Button className="clear-filtros-btn salvar-filtros-btn" onClick={() => onOpenModal?.('excluirFiltro')}>
              <Icon icon="MdDelete" />
            </Button>
          )}
          {filters.map(
            ({ key, placeholder, type, ...rest }) =>
              key.match(/savedFilters/) && (
                <InnerFilter
                  key={key}
                  data={data[key] ?? []}
                  value={savedFilter.value ?? null}
                  placeholder={placeholder as string}
                  isLoading={loading?.[key]}
                  textSearch={rest.textSearch}
                  textLoading={rest.textLoading}
                  type={type}
                  placement={SAVED_FILTER_MENU_PLACEMENT}
                  onSearch={(query: string) => onSearch(key, query)}
                  onSelect={(_v: unknown, item: any) => onSelectSavedFilter(item)}
                  onClose={() => onSearch(key, '')}
                  onClean={onClearAll}
                />
              ),
          )}
        </div>
      </div>
    </div>
    <SalvarFiltrosModal
      show={modals.salvarFiltros}
      onHide={() => onCloseModal?.('salvarFiltros')}
      name={savedFilter.label}
      onSubmit={(name: string, option: 'edit' | 'save') =>
        onSaveFilter?.(
          {
            ...savedFilter,
            id: savedFilter.id,
            name,
          },
          option,
        )
      }
    />
    <ExcluirFiltroModal
      show={modals.excluirFiltro}
      onHide={() => onCloseModal?.('excluirFiltro')}
      filterName={savedFilter.label ?? ''}
      onConfirm={(id: string) => onDeleteFilter?.(id)}
      saveFilter={{
        name: savedFilter.label,
        data: {
          value: savedFilter.value,
          label: savedFilter.label,
          name: savedFilter.name,
        },
      }}
    />
  </>
);

export { FilterSection };
